import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import { useAnalytics } from '../../../hooks/useAnalytics';

const EducatorEmailCaptureModal = ({ open, onClose, colorTheme = 'teal' }) => {
  const theme = useTheme();
  const { trackEvent } = useAnalytics();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [challenge, setChallenge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // User type options
  const userTypeOptions = [
    { value: 'tutor', label: 'Individual Tutor/Teacher' },
    { value: 'educator', label: 'School/Institution Educator' },
    { value: 'administrator', label: 'School Administrator' },
  ];

  // Track when modal opens
  useEffect(() => {
    if (open) {
      trackEvent('Educator Modal Opened', {
        source: 'student_landing'
      });
    }
  }, [open, trackEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !userType || !challenge.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      trackEvent('Educator Waitlist Signup Attempt', {
        userType: userType,
        source: 'educator_modal'
      });

      const response = await fetch('/api/waitlist/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          user_type: userType,
          referral_source: 'student_landing_page',
          challenge_response: challenge.trim(),
          source: 'educator_modal',
          ab_test_data: {
            modal_type: 'educator',
            landing_page: 'student'
          }
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      trackEvent('Educator Waitlist Signup Success', {
        userType: userType
      });

      setSuccess(true);
      
      // Clear form after success
      setTimeout(() => {
        setName('');
        setEmail('');
        setUserType('');
        setChallenge('');
        setSuccess(false);
        onClose();
      }, 3000);

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      trackEvent('Educator Waitlist Signup Error', {
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      trackEvent('Educator Modal Closed', {
        completed: success
      });
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon color="primary" />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Educator Early Access
          </Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Success! Check your email for confirmation. We'll be in touch soon with early access details.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Join our educator waitlist to get early access to powerful exam generation tools designed for educational institutions.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
                
                <TextField
                  fullWidth
                  label="Institution Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                  helperText="Please use your institution email if possible"
                />
                
                <FormControl fullWidth required>
                  <InputLabel>I am a...</InputLabel>
                  <Select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    label="I am a..."
                    disabled={loading}
                  >
                    {userTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="How can you see yourself using this?"
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                  disabled={loading}
                  required
                  placeholder="e.g., For creating practice exams, assessment preparation, homework assignments..."
                  helperText="This helps us understand your needs better"
                />
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading || success}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success}
            startIcon={loading ? <CircularProgress size={20} /> : <SchoolIcon />}
            sx={{
              minWidth: 120,
              background: theme.palette.primary.main,
              '&:hover': {
                background: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? 'Joining...' : 'Join Waitlist'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EducatorEmailCaptureModal;